import { useState } from "react";
import "./Landline.scss";
import {
  RouterIconFi as RouterIcon,
  HelpIcon,
  LockIcon,
  CheckCircleOutlineIcon,
  CableIcon,
  FiberIcon,
  DSLIcon,
  MapPinIcon,
  CheckIcon,
  DownloadIcon,
  UploadIcon,
  PhoneIcon,
  SignalIcon,
  ArrowRightIcon,
  CalendarIcon,
  SparklesIcon,
  InfoIcon,
} from "../../../../../shared/margincalculator/components/icons/Icon";

interface LandlinePlan {
  id: string;
  name: string;
  price: number;
  promoPrice?: number;
  promoMonths?: number;
  download: string;
  upload: string;
  features?: string[];
  recommended?: boolean;
}

const KABEL_PLANS: LandlinePlan[] = [
  {
    id: "k-100",
    name: "Red Business Internet Cable 100",
    price: 29.9,
    promoPrice: 29.9,
    promoMonths: 6,
    download: "100 Mbit/s",
    upload: "50 Mbit/s",
    recommended: true,
  },
  {
    id: "k-100-phone",
    name: "Red Business Internet & Phone Cable 100",
    price: 34.9,
    promoPrice: 34.9,
    promoMonths: 6,
    download: "100 Mbit/s",
    upload: "50 Mbit/s",
    features: ["Telefonie Flatrate"],
  },
  {
    id: "k-300",
    name: "Red Business Internet Cable 300",
    price: 39.9,
    promoPrice: 29.9,
    promoMonths: 6,
    download: "300 Mbit/s",
    upload: "50 Mbit/s",
  },
];

const GLASFASER_PLANS: LandlinePlan[] = [
  {
    id: "g-150",
    name: "Red Business Internet 150",
    price: 39.9,
    promoPrice: 39.9,
    promoMonths: 6,
    download: "150 Mbit/s",
    upload: "50 Mbit/s",
    recommended: true,
  },
  {
    id: "g-300",
    name: "Red Business Internet 300",
    price: 44.9,
    promoPrice: 44.9,
    promoMonths: 6,
    download: "300 Mbit/s",
    upload: "50 Mbit/s",
  },
  {
    id: "g-600",
    name: "Red Business Internet 600",
    price: 54.9,
    promoPrice: 54.9,
    promoMonths: 6,
    download: "600 Mbit/s",
    upload: "50 Mbit/s",
  },
];

function Landline() {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedTech, setSelectedTech] = useState<
    "kabel" | "glasfaser" | "dsl"
  >("kabel");
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(
    KABEL_PLANS.find((p) => p.recommended)?.id || KABEL_PLANS[0].id,
  );
  const [selectedRouterId, setSelectedRouterId] =
    useState<string>("fritz-6690");
  const [isAddressVerified, setIsAddressVerified] = useState(false);
  const [isAddedToOffer, setIsAddedToOffer] = useState(false);

  const [address, setAddress] = useState({
    street: "Musterstraße",
    number: "1",
    zip: "10115",
    city: "Berlin",
    saveToAddressBook: false,
  });

  const activePlans =
    selectedTech === "glasfaser" ? GLASFASER_PLANS : KABEL_PLANS;

  const selectedPlan = activePlans.find((p) => p.id === selectedPlanId);
  const routerPrice = selectedRouterId === "fritz-6690" ? 6.99 : 0;
  const totalPrice = (selectedPlan?.price || 0) + routerPrice;
  const totalPromoPrice =
    (selectedPlan?.promoPrice || selectedPlan?.price || 0) + routerPrice;

  const formatPrice = (price: number) =>
    price.toLocaleString("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const handleTechSelect = (tech: "kabel" | "glasfaser" | "dsl") => {
    if (tech === "dsl") return;
    setSelectedTech(tech);
    // Select recommended plan of the new technology automatically
    const plans = tech === "glasfaser" ? GLASFASER_PLANS : KABEL_PLANS;
    const recommendedPlan = plans.find((p) => p.recommended) || plans[0];
    setSelectedPlanId(recommendedPlan.id);
    setCurrentStep(3);
  };

  const handlePlanSelect = (planId: string) => {
    setSelectedPlanId(planId);
    setCurrentStep(4);
  };

  const handleEditAddress = () => {
    setIsAddressVerified(false);
    setCurrentStep(1);
  };

  const handleVerify = () => {
    setIsAddressVerified(true);
    setCurrentStep(2);
  };

  return (
    <div className="landline">
      <header className="calculator__view-header">
        <h2>Festnetz Konfiguration</h2>
      </header>

      {/* Activation Card */}
      <div className="landline__activation-card">
        <div className="landline__info-group">
          <div className="landline__icon-box">
            <RouterIcon />
          </div>
          <div className="landline__text-content">
            <h3>
              Festnetz & Internet <HelpIcon className="help-icon" />
            </h3>
            <p>
              {isActive
                ? "Adresse prüfen um Tarife zu sehen"
                : "Aktivieren für GigaKombi Business Vorteil"}
            </p>
          </div>
        </div>
        <div className="landline__toggle-wrapper">
          <label className="switch">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <span className="slider"></span>
          </label>
        </div>
      </div>

      {/* Stepper Content (visible when active) */}
      {isActive && (
        <div className="landline__stepper-container">
          {/* Step 1: Installationsadresse */}
          <div
            className={`landline__step ${currentStep === 1 ? "landline__step--active" : ""} ${isAddressVerified ? "landline__step--completed" : ""}`}
          >
            <div className="landline__step-header">
              <div className="step-title-group">
                <div className="step-number">
                  {isAddressVerified ? <CheckIcon /> : "1"}
                </div>
                <span className="step-title">Installationsadresse</span>
              </div>
              {isAddressVerified && (
                <div className="header-actions">
                  <button className="btn-edit" onClick={handleEditAddress}>
                    Ändern
                  </button>
                </div>
              )}
            </div>

            {isAddressVerified && currentStep !== 1 ? (
              <div className="landline__step-summary">
                <div className="summary-address">
                  <MapPinIcon /> {address.street} {address.number},{" "}
                  {address.zip} {address.city}
                </div>
              </div>
            ) : (
              <div className="landline__step-content">
                <div className="landline__address-form">
                  <div className="form-group">
                    <label>Straße</label>
                    <input
                      type="text"
                      value={address.street}
                      onChange={(e) =>
                        setAddress({ ...address, street: e.target.value })
                      }
                      placeholder="Straße"
                    />
                  </div>
                  <div className="form-group">
                    <label>Nr.</label>
                    <input
                      type="text"
                      value={address.number}
                      onChange={(e) =>
                        setAddress({ ...address, number: e.target.value })
                      }
                      placeholder="1"
                    />
                  </div>
                  <div className="form-group">
                    <label>Postleitzahl</label>
                    <input
                      type="text"
                      value={address.zip}
                      onChange={(e) =>
                        setAddress({ ...address, zip: e.target.value })
                      }
                      placeholder="10115"
                    />
                  </div>
                  <div className="form-group">
                    <label>Ort</label>
                    <input
                      type="text"
                      value={address.city}
                      onChange={(e) =>
                        setAddress({ ...address, city: e.target.value })
                      }
                      placeholder="Berlin"
                    />
                  </div>
                  <div className="checkbox-group">
                    <input
                      type="checkbox"
                      id="saveAddress"
                      checked={address.saveToAddressBook}
                      onChange={(e) =>
                        setAddress({
                          ...address,
                          saveToAddressBook: e.target.checked,
                        })
                      }
                    />
                    <label htmlFor="saveAddress">
                      Adresse im Adressbuch speichern
                    </label>
                  </div>
                  <div className="action-row">
                    <button className="btn-verify" onClick={handleVerify}>
                      <CheckCircleOutlineIcon /> Verfügbarkeit prüfen
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Step 2: Technologie Auswahl */}
          <div
            className={`landline__step ${currentStep === 2 ? "landline__step--active" : ""}`}
          >
            <div className="landline__step-header">
              <div className="step-title-group">
                <div className="step-number">2</div>
                <span className="step-title">Technologie wählen</span>
              </div>
              {currentStep < 2 && <LockIcon className="step-lock" />}
            </div>

            {currentStep >= 2 && (
              <div className="landline__step-content">
                <div className="landline__tech-grid">
                  <div
                    className={`landline__tech-card ${selectedTech === "kabel" ? "landline__tech-card--active" : ""}`}
                    onClick={() => handleTechSelect("kabel")}
                  >
                    <div className="tech-icon">
                      <CableIcon />
                    </div>
                    <span className="tech-name">Kabel</span>
                    <span className="tech-speed">1000 Mbit/s</span>
                    <span className="tech-status tech-status--available">
                      Verfügbar
                    </span>
                  </div>

                  <div
                    className={`landline__tech-card ${selectedTech === "glasfaser" ? "landline__tech-card--active" : ""}`}
                    onClick={() => handleTechSelect("glasfaser")}
                  >
                    <div className="tech-icon">
                      <FiberIcon />
                    </div>
                    <span className="tech-name">Glasfaser</span>
                    <span className="tech-speed">1000 Mbit/s</span>
                    <span className="tech-status tech-status--available">
                      Verfügbar
                    </span>
                  </div>

                  <div className="landline__tech-card landline__tech-card--disabled">
                    <div className="tech-icon">
                      <DSLIcon />
                    </div>
                    <span className="tech-name">DSL</span>
                    <span className="tech-speed">Nicht verfügbar</span>
                    <span className="tech-status tech-status--unavailable">
                      Ausbau nicht geplant
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Step 3: Tarif auswählen */}
          <div
            className={`landline__step ${currentStep === 3 ? "landline__step--active" : ""}`}
          >
            <div className="landline__step-header">
              <div className="step-title-group">
                <div className="step-number">3</div>
                <span className="step-title">
                  Tarif auswählen{" "}
                  <span style={{ color: "#777", fontWeight: 400 }}>
                    (
                    {selectedTech.charAt(0).toUpperCase() +
                      selectedTech.slice(1)}
                    )
                  </span>
                </span>
              </div>
              {currentStep < 3 && <LockIcon className="step-lock" />}
            </div>

            {currentStep >= 3 && (
              <div className="landline__step-content">
                <div className="landline__plan-grid">
                  {activePlans.map((plan) => (
                    <div
                      key={plan.id}
                      className={`landline__plan-card ${plan.recommended ? "landline__plan-card--recommended" : ""} ${selectedPlanId === plan.id ? "landline__plan-card--active" : ""}`}
                      onClick={() => handlePlanSelect(plan.id)}
                    >
                      <span className="plan-name">{plan.name}</span>
                      <div className="plan-price-group">
                        <span className="price">
                          {plan.price.toFixed(2).replace(".", ",")}
                        </span>
                        <span className="period">€ / mtl.</span>
                      </div>
                      <div className="plan-details">
                        <div className="detail-item">
                          <DownloadIcon /> Download {plan.download}
                        </div>
                        <div className="detail-item">
                          <UploadIcon /> Upload {plan.upload}
                        </div>
                        {plan.features?.map((f, i) => (
                          <div key={i} className="detail-item">
                            <PhoneIcon /> {f}
                          </div>
                        ))}
                      </div>
                      <button
                        className={`btn-select-plan ${selectedPlanId === plan.id ? "btn-select-plan--active" : ""}`}
                      >
                        {selectedPlanId === plan.id ? (
                          <>
                            <CheckIcon /> Ausgewählt
                          </>
                        ) : (
                          "Auswählen"
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Step 4: Router & Hardware */}
          <div
            className={`landline__step ${currentStep === 4 ? "landline__step--active" : ""}`}
          >
            <div className="landline__step-header">
              <div className="step-title-group">
                <div className="step-number">4</div>
                <span className="step-title">Router & Hardware</span>
              </div>
              {currentStep < 4 && <LockIcon className="step-lock" />}
            </div>

            {currentStep >= 4 && (
              <div className="landline__step-content">
                <div className="landline__router-list">
                  <div
                    className={`landline__router-option ${selectedRouterId === "fritz-6690" ? "landline__router-option--active" : ""}`}
                    onClick={() => setSelectedRouterId("fritz-6690")}
                  >
                    <div className="option-info">
                      <div className="radio-circle">
                        <div className="inner"></div>
                      </div>
                      <div className="text-group">
                        <span className="name">FRITZ!Box 6690 Cable</span>
                        <span className="desc">
                          Premium WLAN AX, 2x Telefon, 4x LAN
                        </span>
                      </div>
                    </div>
                    <div className="option-price">
                      <span className="price">+ 6,99 €</span>
                      <span className="period">mtl. Miete</span>
                    </div>
                  </div>

                  <div
                    className={`landline__router-option ${selectedRouterId === "v-station" ? "landline__router-option--active" : ""}`}
                    onClick={() => setSelectedRouterId("v-station")}
                  >
                    <div className="option-info">
                      <div className="radio-circle">
                        <div className="inner"></div>
                      </div>
                      <div className="text-group">
                        <span className="name">Vodafone Station</span>
                        <span className="desc">
                          Standard WLAN AC, 2x Telefon
                        </span>
                      </div>
                    </div>
                    <div className="option-price">
                      <span className="price price--free">0,00 €</span>
                      <span className="period">Inklusive</span>
                    </div>
                  </div>

                  <div
                    className={`landline__router-option ${selectedRouterId === "own" ? "landline__router-option--active" : ""}`}
                    onClick={() => setSelectedRouterId("own")}
                  >
                    <div className="option-info">
                      <div className="radio-circle">
                        <div className="inner"></div>
                      </div>
                      <div className="text-group">
                        <span className="name">
                          Kein Router (Eigene Hardware)
                        </span>
                        <span className="desc">
                          Kundeneigenes Gerät verwenden
                        </span>
                      </div>
                    </div>
                    <div className="option-price">
                      <span className="price">—</span>
                    </div>
                  </div>
                </div>

                <div className="landline__gigakombi-banner">
                  <SignalIcon className="icon" />
                  <div className="text">
                    <span className="highlight">
                      GigaKombi Business Vorteil:
                    </span>
                    <br />
                    -5€ mtl. auf Mobilfunk + Unlimited Datenvolumen für bis zu
                    10 Business Prime SIMs
                  </div>
                </div>

                <div className="landline__summary-section">
                  <h3 className="summary-title">
                    ZUSAMMENFASSUNG <InfoIcon className="info-icon" />
                  </h3>
                  <div className="summary-row">
                    <span>{selectedPlan?.name}</span>
                    <span className="price">
                      {formatPrice(selectedPlan?.price || 0)} €
                    </span>
                  </div>
                  {routerPrice > 0 && (
                    <div className="summary-row">
                      <span>FRITZ!Box 6690</span>
                      <span className="price">
                        {formatPrice(routerPrice)} €
                      </span>
                    </div>
                  )}
                  <div className="summary-row summary-row--total">
                    <span>Monatlich gesamt</span>
                    <span className="price">{formatPrice(totalPrice)} €</span>
                  </div>
                </div>

                <div className="landline__view-footer">
                  <button
                    className="btn-submit"
                    onClick={() => setIsAddedToOffer(true)}
                  >
                    Festnetz zum Angebot hinzufügen <ArrowRightIcon />
                  </button>
                </div>

                {isAddedToOffer && (
                  <div className="landline__overview">
                    <div className="overview-header">
                      <CalendarIcon /> <span>Preisübersicht</span>
                    </div>
                    <div className="price-cards">
                      <div className="price-card price-card--active">
                        <span className="card-period">Monat 1–6</span>
                        <span className="card-value">
                          {formatPrice(totalPromoPrice)} € <small>€/mtl.</small>
                        </span>
                        {totalPromoPrice < totalPrice && (
                          <span className="card-badge">
                            <SparklesIcon /> Aktionspreis
                          </span>
                        )}
                      </div>
                      <div className="price-card">
                        <span className="card-period">Monat 7–24</span>
                        <span className="card-value">
                          {formatPrice(totalPrice)} € <small>€/mtl.</small>
                        </span>
                        <span className="card-badge card-badge--normal">
                          Normalpreis
                        </span>
                      </div>
                    </div>
                    <div className="avg-row">
                      <div className="avg-left">
                        <span className="avg-icon">
                          <SparklesIcon />
                        </span>
                        <div>
                          <strong>Ø Durchschnitt pro Monat</strong>
                          <p>Berechnet über 24 Monate Laufzeit ⓘ</p>
                        </div>
                      </div>
                      <div className="avg-right">
                        <span className="avg-value">
                          {formatPrice(
                            (totalPromoPrice * 6 + totalPrice * 18) / 24,
                          )}{" "}
                          € <small>€</small>
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Landline;
